import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import LoadThemeComponent from '../../../base/LoadThemeComponent';
import NumberOfDrawsSelect from '../Pick/options/NumberOfDrawsSelect';
import { LineNumbers } from '../Types/Types';
import DrawCounter from '../elements/DrawCounter';
import HiddenInputs from '../elements/HiddenInputs';
import Purchase from '../elements/Purchase';
import PurchaseConfirmation from '../elements/PurchaseConfirmation';
import PurchaseMoreLessButtons from '../elements/PurchaseMoreLessButtons';
import ResellerModule from '../elements/ResellerModule';
import SelectableLotteryDrawDatesModule from '../elements/SelectableLotteryDrawDatesModule';
import { getValidLinesAndStakes } from '../helpers/linesHelper';
import { RootState } from '../redux/store';

import DefaultView from './DefaultView';
import DesktopQuickPickAll from './picker/DesktopQuickPickAll';
import LotteryPicker from './picker/LotteryPicker';
import MobileQuickPickAll from './picker/MobileQuickPickAll';

import PurchasePromoCode from 'components/promo-codes/PurchasePromoCode';
import { isTemplateDelta, isThankYouLotto } from 'utils/template';

function LotteryPlay() {
    const { lottery, lines, stakes, stakeMin, stakeMax, isMobile } = useSelector((state: RootState) => state.playStore);
    const { permutations, banker } = useSelector((state: RootState) => state.lotteryPlayStore);
    const numbersPerLine = lottery.lotteryRule.numbers_per_line;
    const [validLines, setValidLines] = useState<LineNumbers[]>([]);
    const [validStakes, setValidStakes] = useState<number[]>([]);

    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    useEffect(() => {
        const validResults = getValidLinesAndStakes(lottery, lines, stakes, numbersPerLine, stakeMin, stakeMax);
        setValidLines(validResults.validLines);
        setValidStakes(validResults.validStakes);
    }, [lines, stakes]);

    function displayQuickPickAll() {
        if (isTemplateThankYouLotto) {
            return null;
        }

        if (lottery.lotteryRule.is_perms_enabled) {
            return null;
        }

        if (isMobile) {
            return <MobileQuickPickAll />;
        }

        return <DesktopQuickPickAll />;
    }

    function displayPurchaseMoreOrLessButton(className: string) {
        if (isMobile) {
            return null;
        }

        if (isTemplateThankYouLotto) {
            return null;
        }

        if (lottery.lotteryRule.is_perms_enabled) {
            return null;
        }

        return <PurchaseMoreLessButtons className={className} />;
    }

    function displayPurchaseOptions() {
        if (lottery.lotteryRule.is_perms_enabled || isTemplateDelta()) {
            return null;
        }

        return (
            <div className="w-100 purchase-options">
                <div className="row">
                    {lottery.isMultiDrawEnabled ? (
                        <>
                            {!lottery.lotteryRule.is_perms_enabled && (
                                <div className="col-12 col-md-6 pr-md-2 purchase-opt-col user-guide-play-ticket-type">
                                    <NumberOfDrawsSelect />
                                </div>
                            )}
                            <div
                                className={`col-12 col-md-6 pl-md-3 purchase-opt-col ${
                                    lottery.lotteryRule.is_perms_enabled ? 'mx-auto' : ''
                                }`}
                            >
                                <DrawCounter isMobile={isMobile} />
                            </div>
                        </>
                    ) : (
                        <div className="col-12 col-md-12 pl-md-3 purchase-opt-col">
                            <DrawCounter isMobile={isMobile} />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const baseView = () => (
        <>
            {displayQuickPickAll()}
            <LotteryPicker />
            {!isTemplateThankYouLotto && (
                <>
                    {isTemplateDelta() && displayPurchaseMoreOrLessButton('mb-5')}
                    {displayPurchaseOptions()}
                    <PurchasePromoCode />
                    <div className="row no-gutters w-100 d-block d-md-flex justify-content-between align-items-center">
                        {!isTemplateDelta() && displayPurchaseMoreOrLessButton('my-5 mr-5')}
                        <SelectableLotteryDrawDatesModule />
                        {!lottery.lotteryRule.is_perms_enabled && <ResellerModule />}
                        <Purchase />
                    </div>
                </>
            )}
        </>
    );

    return (
        <div className="lottery-play">
            <PurchaseConfirmation />
            <HiddenInputs
                lines={validLines}
                stakes={validStakes}
                permutations={banker === null ? permutations : permutations.filter((num) => num !== banker)}
                bankers={banker === null ? [] : [banker]}
            />
            <LoadThemeComponent defaultView={<DefaultView />} baseView={baseView} />
        </div>
    );
}

export default LotteryPlay;
